import React, { useState } from 'react';
import { HelpCircle } from 'lucide-react';
import GameAlert from './GameAlert';
import { PayoutConfig } from '../utils/instructions';
import { BASIS_POINTS_DIVISOR, scaleDown } from '../utils/scale';
import { MONOPOLY_DECIMALS } from '../../solana-constants';

const PayoutTable = ({ 
  payoutMap,
  rewardPoolBalance = "0" 
}: {
  payoutMap: PayoutConfig[],
  rewardPoolBalance?: string
}) => {
  const [showDiceHelp, setShowDiceHelp] = useState(false);
  const [showPoolHelp, setShowPoolHelp] = useState(false);

  if (!payoutMap?.length) return null;

  // Calculate percentage rewards based on pool size
  const calculateReward = (payout: PayoutConfig) => {
    if (payout.payout_type === 0) {
      return scaleDown(payout.payout_value.toString(), MONOPOLY_DECIMALS)
    } else {
      const poolSize = parseFloat(rewardPoolBalance);
      const percentage = parseFloat((Number(payout.payout_value)/Number(BASIS_POINTS_DIVISOR)).toString());
      const reward = (poolSize * percentage);
      return reward.toFixed(2);
    }
  };

  return (
    <div className="mt-8 p-4 md:p-6 bg-gray-800 rounded-xl animate-fadeIn">
      <h3 className="text-xl md:text-2xl font-bold mb-6 text-center bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">
        Potential Rewards
      </h3>

      {/* Dice Roll Help Alert */}
      {showDiceHelp && (
        <GameAlert>
          <div className="text-gray-300">
            The order of dice matters! For example, rolling a 2 then 3 is different from rolling a 3 then 2. 
            This creates more exciting combinations and winning opportunities.
          </div>
        </GameAlert>
      )}

      {/* Pool Size Help Alert */}
      {showPoolHelp && (
        <GameAlert>
          <div className="text-gray-300">
            Percentage rewards scale with our reward pool size! Currently at {rewardPoolBalance} $MONOPOLY.
            <div className="mt-2">
              🎮 Invite your friends to play and grow the pool together!
            </div>
          </div>
        </GameAlert>
      )}

      <div className="overflow-x-auto rounded-lg">
        <table className="w-full">
          <thead className="bg-gray-700/50">
            <tr>
              <th className="px-4 py-3 text-left flex items-center gap-2">
                Dice Roll
                <button 
                  onClick={() => setShowDiceHelp(!showDiceHelp)}
                  className="text-primary-400 hover:text-primary-300 transition-colors"
                >
                  <HelpCircle className="w-4 h-4" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left flex items-center gap-2">
                Reward
                <button 
                  onClick={() => setShowPoolHelp(!showPoolHelp)}
                  className="text-primary-400 hover:text-primary-300 transition-colors"
                >
                  <HelpCircle className="w-4 h-4" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {payoutMap.map((payout, idx) => (
              <tr 
                key={idx} 
                className="border-b border-gray-700 hover:bg-gray-700/50 transition-colors"
                style={{
                  animation: `fadeIn 0.5s ease-out ${idx * 0.1}s both`
                }}
              >
                <td className="px-4 py-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-primary-600/20 text-primary-400 mr-2">
                    {payout.dice_1}
                  </span>
                  <span className="text-gray-400 mx-1">+</span>
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-accent-600/20 text-accent-400">
                    {payout.dice_2}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-sm ${
                    payout.payout_type === 0 
                      ? 'bg-primary-600/20 text-primary-400'
                      : 'bg-accent-600/20 text-accent-400'
                  }`}>
                    {payout.payout_type === 0 ? 'Fixed' : 'Percentage'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="font-mono text-green-400">
                    {calculateReward(payout)}
                  </span>
                  <span className="text-gray-400 ml-1">$MONOPOLY</span>
                  {payout.payout_type === 1 && (
                    <span className="text-gray-500 ml-2">
                      ({(Number(payout.payout_value)/Number(BASIS_POINTS_DIVISOR)*100).toString()}% of pool)
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PayoutTable;
